import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { referralService } from '../services/referralService';
import { ReferralStats, ReferralLink } from '../types';
import { useToast } from '../contexts/ToastContext';

export const ReferralPage: React.FC = () => {
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralLink, setReferralLink] = useState<ReferralLink | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopying, setIsCopying] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadReferralData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReferralData = async () => {
    try {
      setIsLoading(true);
      const [stats, link] = await Promise.all([
        referralService.getReferralStats(),
        referralService.getReferralLink()
      ]);
      setReferralStats(stats);
      setReferralLink(link);
    } catch (error) {
      console.error('Error loading referral data:', error);
      showToast('Failed to load referral data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      setIsCopying(true);
      const success = await referralService.copyReferralLink();
      if (success) {
        showToast('Referral link copied to clipboard!', 'success');
      } else {
        showToast('Failed to copy referral link', 'error');
      }
    } catch (error) {
      console.error('Error copying referral link:', error);
      showToast('Failed to copy referral link', 'error');
    } finally {
      setIsCopying(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner size="lg" text="Loading referral data..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Referral Program"
          subtitle="Invite friends and earn Int Coins together"
          align="center"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Referral Link Card */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Your Referral Link</h2>
                <Badge variant="success" className="text-sm">
                  {referralStats?.total_referrals || 0} referrals
                </Badge>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Referral Code
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={referralLink?.referral_code || ''}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(referralLink?.referral_code || '');
                        showToast('Referral code copied!', 'success');
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Referral Link
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={referralLink?.referral_link || ''}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCopyLink}
                      loading={isCopying}
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">How it works:</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Share your referral link with friends</li>
                  <li>• They get 20 Int Coins when they sign up</li>
                  <li>• You get 20 Int Coins when they purchase a subscription</li>
                </ul>
              </div>
            </Card>
          </div>

          {/* Stats Card */}
          <div>
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Your Stats</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Total Referrals</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {referralStats?.total_referrals || 0}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Total Earnings</span>
                  <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {referralStats?.total_earnings || 0} coins
                  </span>
                </div>

                {referralStats?.referral_received && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Your Referrer</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      You were referred by {referralStats.referral_received.referrer_email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Joined on {formatDate(referralStats.referral_received.created_at)}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Referrals List */}
        {referralStats?.referrals_made && referralStats.referrals_made.length > 0 && (
          <div className="mt-8">
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Your Referrals</h2>
              
              <div className="overflow-x-auto custom-scrollbar border border-gray-200 dark:border-gray-700 rounded-xl">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Signup Bonus
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Cashback
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {referralStats.referrals_made.map((referral, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {referral.referred_user_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge 
                            variant={
                              referral.status === 'completed' ? 'success' :
                              referral.status === 'signup_bonus_given' ? 'warning' :
                              'secondary'
                            }
                          >
                            {referral.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {referral.signup_bonus_given ? (
                            <span className="text-green-600 dark:text-green-400">✓ 20 coins</span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {referral.cashback_given ? (
                            <span className="text-green-600 dark:text-green-400">✓ 20 coins</span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(referral.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
